import clsx from 'clsx'
import {compact, first, isEmpty, isNumber, last} from 'lodash'
import React, {useCallback, useMemo, useState} from 'react'
import {OverlayTrigger, Popover} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {KTSVG} from '../../../../../gori/components'
import {UsePermission} from '../../../../../gori/hooks'
import {useA1BatchesProvider} from '../../../../../gori/providers'
import {A1_CONSTANTS, A1Helpers} from '../../../a1'
import {FlightInfo} from '../../definitions'
import {durationToString, splitDateTime, splitHours} from '../../utils'

type Props = {
  handleEditConfirmPickupDate?: () => void
  handleEditJourneyInfo?: () => void
  handleOpenDocument?: () => void
}

const JourneyInfoCard: React.FC<Props> = ({
  handleEditConfirmPickupDate,
  handleEditJourneyInfo,
  handleOpenDocument,
}) => {
  const intl = useIntl()
  const {a1Batch} = useA1BatchesProvider()
  const [showPopover, setShowPopover] = useState<Record<string, boolean>>({})
  const {isOrigin, isDestination} = UsePermission()
  const {PICK_UP_DROP_OFF, TRANSFERRED_TO_LOCAL_CARRIER, DELIVERY_STATUS} =
    A1_CONSTANTS.OPTIONS_JOURNEY_STATUS

  const formatNumber = useCallback(
    ({number = 0, showDecimal = true}: {number: string | number; showDecimal?: boolean}) => {
      const numberFormat = Number(number)
      if (!numberFormat && !showDecimal) return null
      return intl.formatNumber(numberFormat, {
        style: 'decimal',
        minimumFractionDigits: showDecimal ? 2 : 0,
        maximumFractionDigits: 2,
      })
    },
    [intl]
  )

  const originFlight: FlightInfo | undefined = useMemo(
    () => first(a1Batch?.journey_flight_infos),
    [a1Batch?.journey_flight_infos]
  )

  const destinationFlight: FlightInfo | null | undefined = useMemo(() => {
    if (a1Batch?.journey_flight_infos?.length <= 1) return null

    return last(a1Batch?.journey_flight_infos)
  }, [a1Batch?.journey_flight_infos])

  const showInfo = useMemo(() => {
    return {
      flights: {
        origin: originFlight,
        destination: destinationFlight,
      },
      label: {
        go: intl.formatMessage({id: originFlight?.atd ? 'ATD' : 'ETD'}),
        arrive: intl.formatMessage({id: destinationFlight?.ata ? 'ATA' : 'ETA'}),
      },
    }
  }, [destinationFlight, intl, originFlight])

  const showJourneyInfoButton = useMemo(() => {
    if (isOrigin) return a1Batch?.journey_status === PICK_UP_DROP_OFF.value[2]
    if (isDestination) {
      const disabledStatus = [TRANSFERRED_TO_LOCAL_CARRIER.value[0], DELIVERY_STATUS.value[0]]

      return !disabledStatus.includes(a1Batch?.journey_status) && a1Batch?.mawb
    }

    return false
  }, [
    DELIVERY_STATUS.value,
    PICK_UP_DROP_OFF.value,
    TRANSFERRED_TO_LOCAL_CARRIER.value,
    a1Batch?.journey_status,
    a1Batch?.mawb,
    isDestination,
    isOrigin,
  ])

  const showShipFrom = useMemo(() => {
    if (isEmpty(a1Batch?.from)) return null
    const {street1, street2, city, state, zip} = a1Batch?.from
    const addressTo = compact([
      street1,
      street2,
      city,
      state && zip ? `${state} ${zip}` : state || zip,
    ]).join(', ')
    return addressTo
  }, [a1Batch.from])

  const toolTipModal = useCallback(
    ({
      message,
      className = '',
      messageTooltip = message,
      classNameTooltip = '',
      placement = 'bottom',
    }) => (
      <OverlayTrigger
        placement={placement}
        show={showPopover.id}
        overlay={
          <Popover
            className='mw-500px p-5'
            onMouseEnter={() => setShowPopover((prev) => ({...prev, tooltip: true}))}
            onMouseLeave={() => setShowPopover((prev) => ({...prev, tooltip: false}))}
          >
            <span className={clsx('fw-bold', classNameTooltip)}>{messageTooltip}</span>
          </Popover>
        }
      >
        <span
          className={clsx('cursor-pointer text-truncate', className)}
          onMouseEnter={() => setShowPopover((prev) => ({...prev, tooltip: true}))}
          onMouseLeave={() => setShowPopover((prev) => ({...prev, tooltip: false}))}
        >
          {message}
        </span>
      </OverlayTrigger>
    ),
    [showPopover]
  )

  const showDocument = useMemo(() => {
    return A1Helpers.checkJourneyStatus({
      statusCurrent: a1Batch?.journey_status,
      statusCheck: A1_CONSTANTS.OPTIONS_JOURNEY_STATUS.PICK_UP_DROP_OFF.value[2],
    })
  }, [a1Batch?.journey_status])

  return (
    <div className='card me-xxl-4 h-100'>
      <div className='card-header border-0 fw-bolder d-flex justify-content-between align-items-center'>
        <div className='d-flex align-items-center col-6 justify-content-between'>
          <span className='fs-3 required'>{intl.formatMessage({id: 'JOURNEY_INFO'})}</span>
        </div>

        <div className='d-flex align-items-center justify-content-end col-6'>
          {showDocument && (
            <div
              className='btn text-blue text-decoration-underline cursor-pointer text-hover-primary'
              onClick={handleOpenDocument}
            >
              {intl.formatMessage({id: 'DOCUMENTS'})}
            </div>
          )}
          {a1Batch?.apu_at && PICK_UP_DROP_OFF.value[0] === a1Batch?.journey_status && (
            <div
              className='btn text-warning text-hover-primary cursor-pointer fw-bolder text-decoration-underline ms-20 p-0'
              onClick={handleEditConfirmPickupDate}
            >
              {intl.formatMessage({id: 'EDIT'})}
            </div>
          )}
          {showJourneyInfoButton && (
            <div
              className='btn text-warning text-hover-primary cursor-pointer fw-bolder text-decoration-underline'
              onClick={handleEditJourneyInfo}
            >
              {intl.formatMessage({id: 'EDIT'})}
            </div>
          )}
        </div>
      </div>
      <div className='card-body bg-light rounded-bottom d-flex flex-column flex-md-row'>
        <div className='col-12 col-md-6'>
          <div className='d-flex pe-md-6 text-primary fs-5 fs-xxl-7 text-center fw-bolder'>
            <div className='col-4'>{intl.formatMessage({id: 'JOURNEY'})}</div>
            <div className='col-4'>{intl.formatMessage({id: 'FLIGHT_NUMBER'})}</div>
            <div className='col-4'>
              {showInfo.label.go} &#8226; {showInfo.label.arrive}
            </div>
          </div>
          <div className='d-flex flex-column min-h-150px mh-275px scroll-y pe-md-4 mt-4'>
            {!isEmpty(a1Batch?.journey_flight_infos) &&
              a1Batch?.journey_flight_infos?.map((flight: FlightInfo, index) => {
                const departureTime = splitDateTime(flight?.atd || flight?.etd || '')
                const arrivalTime = splitDateTime(flight?.ata || flight?.eta || '')

                return (
                  <div
                    className={clsx('d-flex flex-column', {
                      'flex-column-fluid': index !== a1Batch?.journey_flight_infos?.length - 1,
                    })}
                    key={flight.id}
                  >
                    {index === 0 && (
                      <div className='col-4 text-center'>
                        <div className='fs-1 fw-boldest'>{flight?.origin_iata}</div>
                      </div>
                    )}

                    <div className='d-flex flex-fill'>
                      <div className='col-4 d-flex flex-column justify-content-center align-items-center'>
                        <div className='border-dashed border-gray-600 border-2 flex-fill h-100' />
                      </div>
                      <div className='col-4 d-flex align-items-center justify-content-center fw-bolder gap-2'>
                        <KTSVG
                          path='/media/gori/a1/flight.svg'
                          className='svg-icon-1 text-gray-700'
                        />
                        {toolTipModal({
                          message: flight?.flight_number,
                          className: 'fs-4 fs-xxl-6',
                        })}
                      </div>
                      <div className='col-4 d-flex flex-column justify-content-center text-center fs-6 fs-xxl-8 fw-bold gap-2'>
                        {!!departureTime && (
                          <div>
                            <div>{`${departureTime?.date}`}</div>
                            <div className='text-gray-600'>{`${departureTime?.time} ${
                              departureTime?.timezone || ''
                            }`}</div>
                          </div>
                        )}

                        {isNumber(flight?.est_duration) && (
                          <span className='fw-boldest text-dark'>
                            {durationToString(splitHours(flight?.est_duration), intl)}
                          </span>
                        )}

                        {!!arrivalTime && (
                          <div>
                            <div>{`${arrivalTime?.date}`}</div>
                            <div className='text-gray-600'>{`${arrivalTime?.time} ${
                              arrivalTime?.timezone || ''
                            }`}</div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className='col-4 text-center'>
                      <div className='fs-1 fw-boldest'>{flight?.destination_iata}</div>
                    </div>
                  </div>
                )
              })}
          </div>
        </div>
        <span className='border border-secondary my-8 my-md-n8' />
        <div className='col-12 col-md-6 ps-6 fs-5 fs-xxl-7 fw-bold min-h-150px mh-275px scroll-y pe-2'>
          <div className='d-flex flex-wrap'>
            <div className='col-12 d-flex border-bottom border-secondary mt-10 flex-fill mb-1'>
              <span className='text-nowrap'>{intl.formatMessage({id: 'SHIP_FROM'})}</span>
              {showShipFrom &&
                toolTipModal({
                  message: showShipFrom,
                  className: 'ms-3 fs-5 fs-xxl-7 fw-boldest w-75',
                })}
            </div>
            <div className='col-12 col-md-6 d-flex border-bottom border-secondary mt-10 flex-fill mb-1'>
              <span className='text-nowrap'>{intl.formatMessage({id: 'A1_SERVICE'})}</span>
              {a1Batch?.service &&
                toolTipModal({
                  message: a1Batch?.service,
                  className: 'ms-3 fs-5 fs-xxl-7 fw-boldest pe-0 pe-md-4',
                })}
            </div>
            <div className='col-12 col-md-6 d-flex border-bottom border-secondary mt-10 flex-fill mb-1'>
              <span className='text-nowrap'>{intl.formatMessage({id: 'SHIPPER'})}</span>
              {a1Batch?.shipper_info?.shipper_name &&
                toolTipModal({
                  message: a1Batch?.shipper_info?.shipper_name,
                  className: 'ms-3 fs-5 fs-xxl-7 fw-boldest',
                })}
            </div>
            <span className='col-12 col-md-6 d-flex justify-content-between border-bottom border-secondary mt-10 flex-fill mb-1'>
              <span className='text-nowrap'>{intl.formatMessage({id: 'MAWB'})}</span>
              {toolTipModal({
                message: a1Batch?.mawb,
                className: 'ms-3 fw-boldest pe-0 pe-md-4',
              })}
            </span>
            <span className='col-12 col-md-6 d-flex justify-content-between border-bottom border-secondary mt-10 flex-fill mb-1'>
              {toolTipModal({message: intl.formatMessage({id: 'PALLETS'})})}
              {toolTipModal({
                message: formatNumber({number: a1Batch?.pallets, showDecimal: false}),
                className: 'ms-3 fw-boldest',
              })}
            </span>
            <span className='col-12 col-md-6 d-flex justify-content-between border-bottom border-secondary mt-10 flex-fill mb-1'>
              {toolTipModal({message: intl.formatMessage({id: 'PARCELS'})})}
              {toolTipModal({
                message: formatNumber({number: a1Batch?.parcels, showDecimal: false}),
                className: 'ms-3 fw-boldest pe-0 pe-md-4',
              })}
            </span>
            <span className='col-12 col-md-6 d-flex justify-content-between border-bottom border-secondary mt-10 flex-fill mb-1'>
              {toolTipModal({message: intl.formatMessage({id: 'KGS'})})}
              {toolTipModal({
                message: formatNumber({number: a1Batch?.weight}),
                className: 'ms-3 fw-boldest',
              })}
            </span>
            {isOrigin && (
              <>
                <span className='col-12 col-md-6 d-flex justify-content-between border-bottom border-secondary mt-10 flex-fill mb-1'>
                  {toolTipModal({message: intl.formatMessage({id: 'PICK_UP'})})}
                  {toolTipModal({
                    message: formatNumber({number: a1Batch?.origin_pick_up}),
                    className: 'ms-3 fw-boldest',
                  })}
                  <span className='text-muted me-0 me-md-4 ms-2'>
                    {intl.formatMessage({id: 'USD'})}
                  </span>
                </span>
                <span className='col-12 col-md-6 d-flex justify-content-between border-bottom border-secondary mt-10 flex-fill mb-1'>
                  {toolTipModal({message: intl.formatMessage({id: 'CONSOLIDATION'})})}
                  {toolTipModal({
                    message: formatNumber({number: a1Batch?.origin_consolidation}),
                    className: 'ms-3 fw-boldest',
                  })}
                  <span className='text-muted ms-2'>{intl.formatMessage({id: 'USD'})}</span>
                </span>
                <span className='col-12 col-md-6 d-flex justify-content-between border-bottom border-secondary mt-10 flex-fill mb-1'>
                  {toolTipModal({message: intl.formatMessage({id: 'FREIGHT'})})}
                  {toolTipModal({
                    message: formatNumber({number: a1Batch?.origin_freight}),
                    className: 'ms-3 fw-boldest',
                  })}
                  <span className='text-muted me-0 me-md-4 ms-2'>
                    {intl.formatMessage({id: 'USD'})}
                  </span>
                </span>
              </>
            )}
            {isDestination && (
              <>
                <span className='col-12 col-md-6 d-flex justify-content-between border-bottom border-secondary mt-10 flex-fill mb-1'>
                  {toolTipModal({message: intl.formatMessage({id: 'CLEARANCE'})})}
                  {toolTipModal({
                    message: formatNumber({number: a1Batch?.destination_clearance}),
                    className: 'ms-3 fw-boldest',
                  })}
                  <span className='text-muted me-0 me-md-4 ms-2'>
                    {intl.formatMessage({id: 'USD'})}
                  </span>
                </span>
                <span className='col-12 col-md-6 d-flex justify-content-between border-bottom border-secondary mt-10 flex-fill mb-1'>
                  {toolTipModal({message: intl.formatMessage({id: 'DECONSOLIDATION'})})}
                  {toolTipModal({
                    message: formatNumber({number: a1Batch?.destination_deconsolidation}),
                    className: 'ms-3 fw-boldest',
                  })}
                  <span className='text-muted ms-2'>{intl.formatMessage({id: 'USD'})}</span>
                </span>
                <span className='col-12 col-md-6 d-flex justify-content-between border-bottom border-secondary mt-10 flex-fill mb-1'>
                  {toolTipModal({message: intl.formatMessage({id: 'TRANSFER'})})}
                  {toolTipModal({
                    message: formatNumber({number: a1Batch?.destination_transfer}),
                    className: 'ms-3 fw-boldest',
                  })}
                  <span className='text-muted me-0 me-md-4 ms-2'>
                    {intl.formatMessage({id: 'USD'})}
                  </span>
                </span>
              </>
            )}
            {a1Batch?.[`${isOrigin ? 'origin' : 'destination'}_misc`]?.length > 0 &&
              a1Batch?.[`${isOrigin ? 'origin' : 'destination'}_misc`]?.map((misc, index) => {
                return (
                  <span
                    className={clsx(
                      'col-12 col-md-6 d-flex justify-content-between border-bottom border-secondary mt-10 flex-fill mb-1'
                    )}
                    key={index}
                  >
                    {toolTipModal({message: misc.title})}
                    {toolTipModal({
                      message: formatNumber({number: misc.amount}),
                      className: 'ms-3 fw-boldest',
                    })}
                    <span className={clsx('text-muted ms-2', {'me-0 me-md-4': index % 2 !== 0})}>
                      {intl.formatMessage({id: 'USD'})}
                    </span>
                  </span>
                )
              })}
            {a1Batch?.[`${isOrigin ? 'origin' : 'destination'}_misc`]?.length % 2 === 0 && (
              <span className='col-12 col-md-6'></span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export {JourneyInfoCard}
